import { AppEnvironment } from "@/client/core/Core.api";
import { beatsPerSample } from "@/shared/utils/TimeConversion";
import * as pluginNodes from "@/client/core/platform-types/sounds-core/plugin-elementary-nodes";
import { getAppEnvironment } from "@/client/utils/getAppEnvironment";
import { createSignalWithValue } from "@/client/utils/signal";
import type { ElemNode } from "@splice/elemaudio-core";
import { el } from "@splice/elemaudio-core";
import type { StereoNodes, TimeSignature } from "@/client/core/platform-types/labs.api";

export const webBpmSignal = createSignalWithValue(120);
export const webIsPlayingSignal = createSignalWithValue(false);
export const webTimeSignatureSignal = createSignalWithValue<TimeSignature>({
    numerator: 4,
    denominator: 4,
});

const isPlugin = getAppEnvironment() === AppEnvironment.Plugin;

type HostSignalFn = () => ElemNode;

export const hostBpm: HostSignalFn = isPlugin
    ? () => pluginNodes.hostBpm({ key: "plugin:host_bpm" })
    : () => {
          const value = webBpmSignal.value;
          return el.const({ key: "web:host_bpm", value });
      };

export const hostPlaybackState: HostSignalFn = isPlugin
    ? () => pluginNodes.hostPlaybackState({ key: "plugin:host_playback_state" })
    : () => {
          const value = webIsPlayingSignal.value ? 1 : 0;
          return el.const({ key: "web:host_playback_state", value });
      };

// Sampled once at module load — the machine's sample rate is fixed for the session.
const kSampleRate = typeof AudioContext !== "undefined" ? new AudioContext().sampleRate : 44100;

export const hostPpq: HostSignalFn = isPlugin
    ? () => pluginNodes.hostPpq({ key: "plugin:host_ppq" })
    : () => {
          // Convert sample count to beats: samples / sampleRate / 60 * bpm
          // el.time() is monotonically increasing and owned by Elementary — no stale
          // node identity issues unlike el.const-based play state.
          return el.mul(
              el.time(),
              el.const({
                  key: "web:beats_per_sample",
                  value: beatsPerSample(webBpmSignal.value, kSampleRate),
              }),
          );
      };

export const meterBpm: HostSignalFn = isPlugin
    ? hostBpm
    : () => el.const({ key: "meter:host_bpm", value: webBpmSignal.value });

export const meterPlaybackState: HostSignalFn = isPlugin
    ? hostPlaybackState
    : () => {
          const value = webIsPlayingSignal.value ? 1 : 0;
          return el.const({ key: "meter:host_playback_state", value });
      };

type HostTimeSignatureFn = () => StereoNodes;

export const hostTimeSignature: HostTimeSignatureFn = isPlugin
    ? () => {
          return pluginNodes.hostTimeSignature({
              key: "plugin:host_time_signature",
          }) as StereoNodes;
      }
    : () => [
          el.const({
              key: "web:host_beats_per_bar",
              value: webTimeSignatureSignal.value.numerator,
          }),
          el.const({
              key: "web:host_beat_value",
              value: webTimeSignatureSignal.value.denominator,
          }),
      ];

export const meterTimeSignature: HostTimeSignatureFn = isPlugin
    ? hostTimeSignature
    : () => [
          el.const({
              key: "meter:host_beats_per_bar",
              value: webTimeSignatureSignal.value.numerator,
          }),
          el.const({
              key: "meter:host_beat_value",
              value: webTimeSignatureSignal.value.denominator,
          }),
      ];
